import { HTMLTable } from '@blueprintjs/core';
import permissions from '@hogwarts/permissions';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Toggle from 'react-toggle';
import { SettingsPanel } from '../../../components/Settings/SettingsPanel';
import { AnalyticsContext, OrganisationContext } from '../../../context';
import { useMutation, usePermission } from '../../../hooks';
import { TOGGLE_CHECK_ACCOUNT_PREFERENCES } from '../../../mutations';
import { AppToaster } from '../../../utils/toaster';
import { useGetCheckAccountQuery } from '../CheckAccount';

interface CheckConfigProps {
  settings: {
    key: string;
    label: string;
    description: string;
    value: boolean;
    onClick?: () => void;
  }[];
  disabled: boolean;
  handleChange: (key: string) => void;
}

export const CheckConfigComponent = ({
  settings,
  handleChange,
  disabled,
}: CheckConfigProps) => {
  const { t } = useTranslation();

  return (
    <div>
      <HTMLTable interactive striped style={{ width: '100%' }}>
        <thead>
          <tr>
            <th>{t('Name')}</th>
            <th>{t('Description')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {settings?.length === 0 && (
            <tr>
              <td colSpan={3}>
                {t('There appear to be no settings available.')}
              </td>
            </tr>
          )}
          {settings
            ?.map((setting) => {
              return (
                <tr key={setting.key}>
                  <td>{t(setting.label)}</td>
                  <td>{t(setting.description)}</td>
                  <td>
                    <Toggle
                      disabled={disabled}
                      checked={setting.value}
                      onChange={() => handleChange(setting.key)}
                    />
                  </td>
                </tr>
              );
            })
            .filter(Boolean)}
        </tbody>
      </HTMLTable>
    </div>
  );
};

export const CheckConfigContainer = () => {
  const { t } = useTranslation();
  const organisation = useContext(OrganisationContext);
  const analytics = useContext(AnalyticsContext);
  const canToggleCheckAccountPreferences = usePermission(
    permissions.TOGGLE_CHECK_ACCOUNT_PREFERENCES,
    organisation.id
  );

  const { data: checkAccount, refetch } = useGetCheckAccountQuery();

  // Define settings as an array of objects
  const settings = [
    {
      key: 'togglePORequired',
      label: t('Require PO?'),
      description: t(
        'Purchase Order number will be required for future check orders.'
      ),
      value: checkAccount?.checkAccount?.poRequired ?? false,
      onClick: () => {
        try {
          toggleCheckAccountPreferences({
            variables: {
              organisationId: organisation.id,
              key: 'poRequired',
            },
          });
        } catch (error) {
          AppToaster.show({
            icon: 'error',
            intent: 'danger',
            message: 'Unable to update preferences.',
          });
        }
      },
    },
  ];

  const handleChange = async (key: string) => {
    const setting = settings.find((setting) => setting.key === key);
    analytics.events.checks.preferencesUpdated({
      key,
      value: !setting?.value,
    });
    if (setting?.onClick) {
      setting.onClick();
    }
  };

  const [toggleCheckAccountPreferences] = useMutation(
    TOGGLE_CHECK_ACCOUNT_PREFERENCES,
    {
      selector: 'toggleCheckAccountPreferences',
      onCompleted(key) {
        analytics.events.checks.toggleCheckAccountPreferences({
          key,
        });
        refetch();
      },
    }
  );

  if (!checkAccount?.checkAccount) {
    return null;
  }

  return (
    <SettingsPanel title={t('Preferences')} fixedHeight="500px">
      <CheckConfigComponent
        settings={settings}
        handleChange={handleChange}
        disabled={!canToggleCheckAccountPreferences}
      />
    </SettingsPanel>
  );
};

export default CheckConfigContainer;
